import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { ScrollReveal } from '@/components/base/ScrollReveal';

const aboutPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': 'https://cinva.sonbook.net/about#webpage',
  url: 'https://cinva.sonbook.net/about',
  name: '關於清文華泉',
  description:
    '了解清文華泉以彩色知識閱讀為特色的品牌定位，出版內容涵蓋科普、歷史及AIGC輔助視覺書籍。',
  inLanguage: 'zh-TW',
  isPartOf: {
    '@id': 'https://cinva.sonbook.net/#website',
  },
  about: {
    '@id': 'https://cinva.sonbook.net/#organization',
  },
  breadcrumb: {
    '@id': 'https://cinva.sonbook.net/about#breadcrumb',
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  '@id': 'https://cinva.sonbook.net/about#breadcrumb',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: '首頁',
      item: 'https://cinva.sonbook.net/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: '品牌介紹',
      item: 'https://cinva.sonbook.net/about',
    },
  ],
};

export default function About() {
  useEffect(() => {
    document.title = '關於清文華泉｜彩色科普、歷史書籍與品牌定位';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      'content',
      '了解清文華泉以彩色知識閱讀為特色的品牌定位，出版內容涵蓋科普、歷史及AIGC輔助視覺書籍，並提供正式書目與合作購書通路資訊。',
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://cinva.sonbook.net/about');

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', '關於清文華泉｜彩色科普與歷史知識閱讀');

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute(
      'content',
      '認識清文華泉以彩色知識書籍為特色，涵蓋科普、歷史及AIGC輔助視覺內容的出版定位。',
    );

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', 'https://cinva.sonbook.net/about');

    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', '關於清文華泉｜彩色科普與歷史知識閱讀');
    }

    const twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) {
      twitterDesc.setAttribute(
        'content',
        '認識清文華泉以彩色知識書籍為特色，涵蓋科普、歷史及AIGC輔助視覺內容的出版定位。',
      );
    }
  }, []);

  return (
    <>
      <JsonLd data={aboutPageSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <main className="bg-gradient-to-b from-background-50 via-primary-50/30 to-secondary-50/40">

        {/* Intro Section — 不修改 */}
        <ScrollReveal direction="up">
          <section className="w-full pt-28 pb-14 md:pt-36 md:pb-16">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-300 rounded-full mb-6">
                品牌介紹
              </span>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                關於清文華泉
              </h1>
              <p className="text-lg md:text-xl font-heading font-semibold text-foreground-800 mb-6">
                讓知識透過色彩與圖像，更容易被理解
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                清文華泉以彩色知識閱讀為主要特色，出版內容涵蓋科普、歷史及其他已由正式書目確認的知識主題，透過文字、圖像與版面設計，協助讀者理解不同內容。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-8">
                本品牌委託崧博出版經銷，官方網站由崧博出版製作。本頁提供品牌定位、出版方向、內容特色與正式書目查詢資訊。
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-10">
                <a
                  href="https://catalog.sonbook.net/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  查詢完整書目
                  <i className="ri-external-link-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-foreground-300/60 text-foreground-700 rounded-full text-sm font-medium hover:bg-foreground-950/5 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  認識清文華泉
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="pt-8 border-t border-primary-200/30">
                <div className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-4">
                  <p>
                    清文華泉以彩色知識書籍為主要特色，出版方向涵蓋科普、歷史及其他已確認的知識內容。
                  </p>
                  <p>
                    本品牌委託崧博出版經銷，官方網站由崧博出版製作。品牌網站提供正式品牌介紹、出版書籍、合作購書通路及崧博出版書籍資料庫入口。
                  </p>
                  <p>
                    完整書名、作者、ISBN與出版日期請以正式書目資料為準；價格、版本、庫存與購買方式則以各合作購書通路頁面為準。
                  </p>
                </div>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* 認識清文華泉 — 不修改 */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-300 rounded-full mb-5">
                品牌簡介
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                認識清文華泉
              </h2>
              <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4 mb-6">
                <p>
                  清文華泉以彩色知識閱讀為主要特色，透過文字、圖像、版面與色彩呈現科普、歷史及其他已確認的知識主題。
                </p>
                <p>
                  品牌重視知識內容的可讀性與視覺理解，希望讓讀者不只閱讀文字，也能透過圖像與編排掌握主題脈絡。
                </p>
                <p>
                  完整書名、作者、ISBN與出版日期等資料，請以崧博出版書籍資料庫及正式公開書目為準。
                </p>
              </div>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                適合對科普、歷史、自然知識與彩色閱讀內容感興趣，並希望透過文字與圖像理解不同主題的讀者。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                讀者可依網站目前公開的書籍分類與正式書目，查找符合個人興趣與閱讀需求的出版作品。
              </p>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* 品牌定位 — 不修改 */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-300 rounded-full mb-5">
                品牌定位
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                清文華泉的品牌定位
              </h2>
              <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4 mb-8">
                <p>
                  清文華泉聚焦彩色知識出版，以科普與歷史內容為主要方向，透過圖像化、色彩化與通俗化的閱讀呈現，協助讀者理解不同知識主題。
                </p>
                <p>
                  品牌並非單純追求視覺效果，而是讓圖像、色彩與版面服務於內容理解，使複雜資訊能以較清楚的結構呈現。
                </p>
              </div>
              <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-800 mb-4">
                清文華泉的內容呈現原則
              </h3>
              <div className="space-y-5">
                <div>
                  <h4 className="text-sm md:text-base font-semibold text-foreground-800 mb-1.5">
                    知識為核心
                  </h4>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    視覺內容應服務於知識理解，不應只作為與正文無關的裝飾。
                  </p>
                </div>
                <div>
                  <h4 className="text-sm md:text-base font-semibold text-foreground-800 mb-1.5">
                    圖文相互補充
                  </h4>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    文字負責說明概念與脈絡，圖像則協助呈現情境、結構與視覺關係。
                  </p>
                </div>
                <div>
                  <h4 className="text-sm md:text-base font-semibold text-foreground-800 mb-1.5">
                    正式資料為依據
                  </h4>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    書名、作者、ISBN與出版日期以正式書目為準；個別內容與圖像說明則以正式出版品為準。
                  </p>
                </div>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* 出版內容方向 — 不修改 */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <span className="inline-block text-xs tracking-[0.15em] text-secondary-600 px-3 py-1 border border-secondary-300 rounded-full mb-5">
                出版內容
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                清文華泉的出版內容
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-8">
                清文華泉目前以彩色知識書籍、科普類與歷史類內容為主要出版方向，實際分類仍以網站現有正式書籍與書目資料為準。
              </p>
              <div className="space-y-6 mb-8">
                <div>
                  <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-800 mb-2">
                    彩色知識書籍
                  </h3>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    透過色彩、圖像與版面結構呈現知識內容，協助讀者辨識重點、理解關係並建立閱讀脈絡。
                  </p>
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-800 mb-2">
                    科普類內容
                  </h3>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    以一般讀者較容易理解的方式，介紹自然現象、科學知識、生命、環境或其他已由正式書目確認的科普主題。
                  </p>
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-800 mb-2">
                    歷史類內容
                  </h3>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    透過歷史事件、人物、文化與社會變化，協助讀者理解不同時代的背景與發展脈絡。
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  瀏覽彩色知識書籍
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href="https://catalog.sonbook.net/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-foreground-300/60 text-foreground-700 rounded-full text-sm font-medium hover:bg-foreground-950/5 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  查詢完整書目
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* 彩色閱讀特色 — 不修改 */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-200 rounded-full mb-5">
                閱讀特色
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
                清文華泉的彩色閱讀特色
              </h2>
              <div className="space-y-5">
                <div>
                  <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-800 mb-2">
                    以色彩整理資訊
                  </h3>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    透過色彩層級、區塊與視覺提示，協助讀者辨識主題與內容重點。
                  </p>
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-800 mb-2">
                    以圖像輔助理解
                  </h3>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    透過插圖、示意圖或其他視覺內容，補充文字難以直接呈現的概念與情境。
                  </p>
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-800 mb-2">
                    以版面建立脈絡
                  </h3>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    透過篇章安排、資訊層級與視覺節奏，讓內容之間的關係更容易被辨識。
                  </p>
                </div>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* 與崧博出版的關係 — 不修改 */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-200 rounded-full mb-5">
                合作關係
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                清文華泉與崧博出版
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                本品牌委託崧博出版經銷，官方網站由崧博出版製作。讀者可透過清文華泉品牌網站瀏覽相關出版內容，並前往崧博出版書籍資料庫查詢完整書目資訊。
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://sonbook.net/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  前往崧博出版
                  <i className="ri-external-link-line"></i>
                </a>
                <a
                  href="https://catalog.sonbook.net/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-background-200 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-100 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  查詢完整書目
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* 查詢清文華泉正式書目 */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-300 rounded-full mb-5">
                書目查詢
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                查詢清文華泉正式書目
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                如需確認清文華泉出版作品的完整書名、作者、ISBN、出版日期及其他正式出版資訊，請前往崧博出版書籍資料庫查詢。
              </p>
              <a
                href="https://catalog.sonbook.net/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                查詢完整書目
                <i className="ri-external-link-line"></i>
              </a>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* 合作購書通路 */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-200 rounded-full mb-5">
                購書通路
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                查看清文華泉合作購書通路
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                清文華泉相關書籍可透過網站已建立的合作購書通路查找。各作品在不同平台的上架狀態、版本、價格及庫存可能不同，實際資訊請以各通路頁面為準。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                書籍價格、折扣、版本、庫存、配送與到貨資訊可能因平台而異，請以各合作購書通路頁面及實際結帳資訊為準。
              </p>
              <Link
                to="/stores"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                查看合作購書通路
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* 品牌經銷與合作洽詢 */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-300 rounded-full mb-5">
                合作洽詢
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                品牌經銷與合作洽詢
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                如有清文華泉品牌經銷、通路合作、內容合作、媒體或其他相關合作需求，請透過網站現有的官方聯絡方式提出詢問。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                品牌經銷與合作方案詳情請直接聯繫洽詢。
              </p>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* 頁面收尾 CTA */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                從清文華泉，進入彩色知識閱讀
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                透過清文華泉目前公開的科普、歷史與彩色知識出版內容，認識品牌定位、查詢正式書目，並由合作購書通路找到適合的作品。
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  瀏覽彩色知識書籍
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/stores"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-foreground-300/60 text-foreground-700 rounded-full text-sm font-medium hover:bg-foreground-950/5 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  查看合作購書通路
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* 頁面底部 GEO 摘要 */}
        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>
        <section className="w-full py-10 md:py-12 pb-20 md:pb-28">
          <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
            <div className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-4">
              <p>
                清文華泉以彩色知識書籍為主要特色，出版內容涵蓋科普、歷史及其他已確認的知識主題。
              </p>
              <p>
                部分出版內容使用AIGC作為視覺呈現的輔助方式，不代表所有文字、圖像或知識內容均由人工智慧自動生成。
              </p>
              <p>
                本品牌委託崧博出版經銷，官方網站由崧博出版製作。網站提供正式品牌介紹、出版書籍、合作購書通路及崧博出版書籍資料庫入口。
              </p>
              <p>
                完整書目請以崧博出版書籍資料庫為準；價格、版本、庫存、配送及購買方式則以各合作購書通路頁面為準。
              </p>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}